import { useMemo, useState } from 'react'
import { generateChart } from '@/lib/ziwei/algorithm'
import type { BirthInfo, Palace, ZiweiChart } from '@/lib/ziwei/types'
import { BRANCHES, SHICHEN, STEMS } from '@/lib/ziwei/constants'
import ChartBoard from '@/components/ChartBoard'

const DIMS: { key: string; label: string }[] = [
  { key: 'overview', label: '命格' },
  { key: 'personality', label: '性格' },
  { key: 'love', label: '感情' },
  { key: 'career', label: '事业' },
  { key: 'wealth', label: '财运' },
  { key: 'health', label: '健康' },
  { key: 'family', label: '兄弟' },
  { key: 'children', label: '子女' },
  { key: 'move', label: '迁移' },
  { key: 'friends', label: '人际' },
  { key: 'home', label: '田宅' },
  { key: 'spirit', label: '福德' },
  { key: 'parents', label: '父母' },
]

type InterpPayload = {
  k: string
  y: number
  m: number
  d: number
  h: number
  g: string
  ms?: string | null
  c: Record<string, string>
  t?: Record<string, number>
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function chartKey(info: BirthInfo) {
  return `${info.year}-${pad2(info.month)}-${pad2(info.day)}-${info.hour}-${info.gender}`
}

export default function App() {
  const [year, setYear] = useState(1990)
  const [month, setMonth] = useState(1)
  const [day, setDay] = useState(1)
  const [hour, setHour] = useState(0)
  const [gender, setGender] = useState<'male' | 'female'>('male')
  const [chart, setChart] = useState<ZiweiChart | null>(null)
  const [selected, setSelected] = useState<Palace | null>(null)
  const [chartError, setChartError] = useState<string | null>(null)

  const [interp, setInterp] = useState<InterpPayload | null>(null)
  const [interpStatus, setInterpStatus] = useState<'idle' | 'loading' | 'ok' | 'missing' | 'error'>('idle')
  const [interpMsg, setInterpMsg] = useState('')
  const [dim, setDim] = useState('overview')

  const [question, setQuestion] = useState('请根据命盘与断语，简要说说命主行事风格。')
  const [aiOut, setAiOut] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [aiErr, setAiErr] = useState('')

  const birthInfo: BirthInfo = useMemo(
    () => ({ year, month, day, hour, gender }),
    [year, month, day, hour, gender],
  )

  function drawChart() {
    setChartError(null)
    setInterp(null)
    setInterpStatus('idle')
    setInterpMsg('')
    setAiOut('')
    setAiErr('')
    try {
      const c = generateChart(birthInfo)
      setChart(c)
      setSelected(c.palaces.find((p) => p.isMingGong) ?? null)
    } catch (e) {
      setChart(null)
      setChartError(e instanceof Error ? e.message : String(e))
    }
  }

  async function loadInterp(info: BirthInfo = birthInfo) {
    setInterpStatus('loading')
    setInterpMsg('')
    setInterp(null)
    const q = new URLSearchParams({
      y: String(info.year),
      m: String(info.month),
      d: String(info.day),
      h: String(info.hour),
      g: info.gender,
    })
    try {
      const res = await fetch(`/api/interp?${q}`)
      const body = await res.json()
      if (res.status === 404) {
        setInterpStatus('missing')
        setInterpMsg(body?.error || 'R2 中无此命盘解读（样本年仅含少量 keys）')
        return
      }
      if (!res.ok) {
        setInterpStatus('error')
        setInterpMsg(body?.error || `HTTP ${res.status}`)
        return
      }
      setInterp(body as InterpPayload)
      setInterpStatus('ok')
      setDim('overview')
    } catch (e) {
      setInterpStatus('error')
      setInterpMsg(e instanceof Error ? e.message : String(e))
    }
  }

  async function askAi() {
    if (!chart) return
    setAiBusy(true)
    setAiErr('')
    setAiOut('')
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          birth: birthInfo,
          chartSummary: {
            key: chartKey(birthInfo),
            wuxingJuName: chart.wuxingJuName,
            mingZhu: chart.mingZhu,
            shenZhu: chart.shenZhu,
            yinYangGender: chart.yinYangGender,
            mingGong: chart.palaces.find((p) => p.isMingGong)?.stars.map((s) => s.name) ?? [],
          },
          interpExcerpt: interp
            ? {
                k: interp.k,
                ms: interp.ms,
                overview: (interp.c?.overview || '').slice(0, 2500),
                dimKey: dim,
                dimText: (interp.c?.[dim] || '').slice(0, 2500),
              }
            : null,
        }),
      })
      const body = await res.json()
      if (!res.ok) {
        setAiErr(body?.error || `HTTP ${res.status}`)
        return
      }
      setAiOut(body.reply || body.text || JSON.stringify(body, null, 2))
    } catch (e) {
      setAiErr(e instanceof Error ? e.message : String(e))
    } finally {
      setAiBusy(false)
    }
  }

  return (
    <div className="app">
      <header className="hero">
        <h1>紫微斗数 · 排盘 MVP</h1>
        <p>
          浏览器内排盘（MIT 引擎）· R2 断语 · Cloudflare AI Gateway。时辰为序号 0–11（子=0…亥=11），未做真太阳时。
        </p>
      </header>

      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault()
          drawChart()
        }}
      >
        <label>
          公历年
          <input
            type="number"
            min={1900}
            max={2100}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <label>
          月
          <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value))} />
        </label>
        <label>
          日
          <input type="number" min={1} max={31} value={day} onChange={(e) => setDay(Number(e.target.value))} />
        </label>
        <label>
          时辰（0–11）
          <select value={hour} onChange={(e) => setHour(Number(e.target.value))}>
            {SHICHEN.map((s) => (
              <option key={s.branch} value={s.branch}>
                {s.branch} · {s.name}（{BRANCHES[s.branch]} · {s.range}）
              </option>
            ))}
          </select>
        </label>
        <label>
          性别
          <select value={gender} onChange={(e) => setGender(e.target.value as 'male' | 'female')}>
            <option value="male">男</option>
            <option value="female">女</option>
          </select>
        </label>
        <div className="actions">
          <button className="btn" type="submit">
            起盘
          </button>
          <button
            className="btn secondary"
            type="button"
            onClick={() => {
              drawChart()
              void loadInterp(birthInfo)
            }}
          >
            起盘并拉取解读
          </button>
          <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>key: {chartKey(birthInfo)}</span>
        </div>
      </form>

      {chartError ? <p className="err">{chartError}</p> : null}

      {chart ? (
        <>
          <div className="meta">
            <span className="chip">{chart.wuxingJuName}</span>
            {chart.yinYangGender ? <span className="chip">{chart.yinYangGender}</span> : null}
            {chart.mingZhu ? <span className="chip">命主 {chart.mingZhu}</span> : null}
            {chart.shenZhu ? <span className="chip">身主 {chart.shenZhu}</span> : null}
            <button className="btn secondary" type="button" onClick={() => void loadInterp()}>
              从 R2 加载解读
            </button>
          </div>

          <div className="board-wrap">
            <ChartBoard chart={chart} onPalaceSelect={setSelected} />
            <aside className="side">
              <h3>宫位 / 解读 / AI</h3>
              {selected ? (
                <div className="interp-body">
                  <strong>{selected.name}</strong>（{STEMS[selected.stem]}{BRANCHES[selected.branch]}）
                  {'\n'}
                  {selected.stars.map((s) => `${s.name}${s.siHua ? `[${s.siHua}]` : ''}${s.brightnessRaw ? `(${s.brightnessRaw})` : ''}`).join(' · ') ||
                    '（空宫）'}
                </div>
              ) : (
                <p className="interp-body" style={{ color: 'var(--muted)' }}>
                  点击宫位查看星曜
                </p>
              )}

              {interpStatus === 'loading' ? <p className="ok">加载解读中…</p> : null}
              {interpStatus === 'missing' || interpStatus === 'error' ? <p className="err">{interpMsg}</p> : null}

              {interp ? (
                <>
                  <div className="dim-tabs">
                    {DIMS.map((d) => (
                      <button
                        key={d.key}
                        type="button"
                        className={dim === d.key ? 'active' : ''}
                        onClick={() => setDim(d.key)}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="interp-body">{interp.c?.[dim] || '（无此维）'}</div>
                </>
              ) : null}

              <div className="ai-box">
                <h3 style={{ margin: '8px 0 6px' }}>AI Gateway</h3>
                <textarea value={question} onChange={(e) => setQuestion(e.target.value)} />
                <button className="btn" type="button" disabled={aiBusy} onClick={() => void askAi()} style={{ marginTop: 8 }}>
                  {aiBusy ? '请求中…' : '提问'}
                </button>
                {aiErr ? <p className="err">{aiErr}</p> : null}
                {aiOut ? <pre className="ai-out">{aiOut}</pre> : null}
              </div>
            </aside>
          </div>
        </>
      ) : null}

      <footer className="footer">
        <p>
          <strong>本项目使用了 紫微斗数开源解读数据集 v4.0</strong>（674,976 张命盘 × 13
          维）。来源：{' '}
          <a href="https://github.com/Renhuai123/ziwei-doushu" target="_blank" rel="noreferrer">
            https://github.com/Renhuai123/ziwei-doushu
          </a>{' '}
          作者：王多鱼AI。排盘引擎代码（MIT）vendored from <code>lib/ziwei/</code>。
        </p>
        <p>
          仅供娱乐与学习参考，不构成任何决策建议。数据集口径：按时辰序号排盘、未做真太阳时校正。
        </p>
      </footer>
    </div>
  )
}
